import {Component, HostListener, inject, Input, OnInit} from '@angular/core';
import { AuthSessionService } from '../../services/auth-session.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiUrl } from '../../shared/models/apiurl.models';
import { LoginAction, LoginService } from '../session/login/login.service';
import { SystemApp } from '../../constans/SystemConfigurations';
import { MainCategory } from '../../models/header.model';
import { AppService } from '../../app.service';
import { Router } from "@angular/router";
import { CartService } from '../../services/cart.service';
import { AccountSummaryService } from '../../services/account-summary.service';

// ── Icon Maps ─────────────────────────────────────────────────────────────────

/**
 * Main-category icons as inline SVG path data (24x24 viewBox, stroked).
 * Deliberately NOT emoji: emoji render differently per-OS, can't inherit
 * currentColor, and don't match the stroked SVG style already used across
 * the listing components. Subcategory icons below are still emoji — those
 * live in the mega-menu/drawer only and have no clean icon equivalent.
 */
const CATEGORY_ICON_PATHS: Record<string, string> = {
  // car
  CAR:            'M5 17h14M5 17a2 2 0 11-4 0 2 2 0 014 0zm14 0a2 2 0 11-4 0 2 2 0 014 0zM3 17v-4.5L5 7h14l2 5.5V17M6 12h12',
  // house
  NADLAN:         'M3 10.5L12 3l9 7.5M5.5 9.5V20h13V9.5M9.5 20v-6h5v6',
  // shopping bag
  SALE2:          'M6 7h12l-1 13H7L6 7zm3 0V5a3 3 0 116 0v2',
  // briefcase
  REQUIRED:       'M3 8.5h18v11H3v-11zm6 0V6a2 2 0 012-2h2a2 2 0 012 2v2.5M3 13h18',
  // office building
  NADLANBUSINESS: 'M4 21V4.5h9V21M13 10h7v11M7 8h3M7 12h3M7 16h3M16 14h1M16 18h1',
  // wrench
  COWORKER:       'M14.5 6a3.5 3.5 0 004.6 4.3l-8.4 8.4a2.4 2.4 0 01-3.4-3.4l8.4-8.4A3.5 3.5 0 0014.5 6z',
  // phone
  CELLULAR:       'M7 3.5h10v17H7v-17zm3.5 14h3',
  // paw
  PET:            'M12 21c-1.5 0-6-3.5-8-7.5C2 9 4 4 8.5 4c2 0 3 1.5 3.5 2.5C12.5 5.5 13.5 4 15.5 4 20 4 22 9 20 13.5 18 17.5 13.5 21 12 21z',
  // book
  TEACHER:        'M4 4.5h6a2.5 2.5 0 012.5 2.5v13A2 2 0 0011 18.5H4v-14zm16 0h-6A2.5 2.5 0 0011.5 7v13A2 2 0 0113 18.5h7v-14z',
  // sofa
  FURNITURE:      'M4 11V8a2 2 0 012-2h12a2 2 0 012 2v3M2.5 11.5a1.5 1.5 0 013 0V15h13v-3.5a1.5 1.5 0 013 0V18H2.5v-6.5zM6 18v2M18 18v2',
  // dress / hanger
  FASHION:        'M12 3a2 2 0 00-1 3.7L3.5 15h17L13 6.7A2 2 0 0012 3zM3.5 15v4h17v-4',
  // teddy / child
  BOYBABY:        'M12 13.5a4.5 4.5 0 100-9 4.5 4.5 0 000 9zm0 0v7M8 8.5a2 2 0 11-2.8-1.8M16 8.5a2 2 0 102.8-1.8M8 20.5h8',
};

/** Fallback: generic box outline for any category not mapped above. */
const CATEGORY_ICON_FALLBACK = 'M3.5 7.5L12 3l8.5 4.5v9L12 21l-8.5-4.5v-9zM12 12l8.5-4.5M12 12v9M12 12L3.5 7.5';

const SUBCATEGORY_ICONS: Record<string, string> = {
  // רכב
  PRIVATE:     '🚗',
  VANS:        '🚐',
  MOTORCYCLE:  '🏍️',
  JEEP:        "🚙",
  TRUCKS:      '🚚',
  SCOOTERS:    '🛵',
  VESSEL:      '⛵',
  SPECIAL:     '🚜',
  // נדל"ן
  BUYING:      '🏢',
  RENTAL:      '🔑',
  PARTNERS:    '👥',
  COMMERCIAL:  '🏪',
  // יד שניה
  CRAFTSMANSHIP:                     '🎨',
  BICYCLE:                           '🚴',
  FASHION_AND_GROOMING:              '👗',
  COLLECTIBLES:                      '🪙',
  GARAGE_SALE:                       '📦',
  JUDAICA:                           '🕎',
  MUSICAL_INSTRUMENTS:               '🎸',
  WEAPON:                            '🔫',
  TOOLS:                             '🔧',
  SANITARY_WARE:                     '🚿',
  FOR_THE_HOME:                      '🏠',
  FOR_THE_GARDEN:                    '🪴',
  FOR_THE_TRAVELER_AND_THE_RECRUIT:  '🎒',
  FOR_DELIVERY_EVERYTHING_IS_FREE:   '🎁',
  FOR_BABY_AND_CHILD:                '🧸',
  PORTABLE_BUILDINGS:                '🏗️',
  MUSIC_AND_MOVIES:                  '🎵',
  ELECTRICAL_PRODUCTS:               '⚡',
  COMPUTERS_AND_RELATED_EQUIPMENT:   '💻',
  MASSAGE_DEVICES:                   '💆',
  PROTECTION_FOR_HOME_AND_BUSINESS:  '🔒',
  SUBSCRIPTIONS_TICKETS:             '🎟️',
  GAMES_AND_TOYS:                    '🎮',
  CLOTHES_HANGERS_AND_LAUNDRY:       '👕',
  STOCKS:                            '📊',
  STEREO:                            '🔊',
  CELL_PHONE:                        '📱',
  SPORT:                             '⚽',
  LITERATURE_STUDY_MATERIALS:        '📚',
  BUSINESSES_FOR_SALE__DELIVERY:     '🏬',
  EVENT_EQUIPMENT:                   '🎪',
  DJ_AND_STUDIO_EQUIPMENT:           '🎛️',
  BUSINESS_EQUIPMENT:                '💼',
  INDUSTRIAL_EQUIPMENT:              '🏭',
  SCHOOL_SUPPLIES:                   '🏫',
  EQUIPMENT_FOR_RENT:                '🔑',
  OFFICE_SUPPLIES:                   '🖥️',
  NURSING_MEDICAL_EQUIPMENT:         '🏥',
  PHOTOGRAPHY:                       '📷',
  GAME_CONSOLES:                     '🕹️',
  FURNITURE:                         '🛋️',
  CREDIT_CARDS_COUPONS_MISCELLANEOUS:'🏷️',
  COMMUNICATION:                     '📡',
  LIGHTING_AND_CHANDELIERS:          '💡',
  // misc
  REQUIRED:   '💼',
  COWORKER:   '🔧',
  PET:        '🐾',
  TEACHER:    '📚',
};

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit {
  private readonly authSession = inject(AuthSessionService);
  private readonly accountSummary = inject(AccountSummaryService);
  router = inject(Router);
  // State
  mobileMenuOpen   = false;
  allCatsOpen      = false;
  mainCategories!: MainCategory[];
  hoveredCategory: any = null;
  hoveredSub: any = null;

  /**
   * Announcement bar text. Empty by default so the bar takes up NO vertical
   * space unless there's a real, active announcement to show — previously
   * this was hardcoded `true` with a fixed promo string, so every visitor
   * permanently paid ~40px of header height for a stale message.
   * Set from a parent/config when a live announcement exists.
   */
  @Input() announcement: string | null = null;

  /** Optional short badge shown before the announcement text (e.g. "חדש"). */
  @Input() announcementBadge: string | null = null;

  private announcementDismissed = false;

  get showAnnouncement(): boolean {
    return !!this.announcement && !this.announcementDismissed;
  }

  dismissAnnouncement(): void {
    this.announcementDismissed = true;
  }

  // ── Search ──────────────────────────────────────────────────────────────────
  searchQuery = '';

  /** Opens the dedicated search experience as soon as the header field is selected. */
  openSearchPage(): void {
    const q = this.searchQuery.trim();
    this.router.navigate(['/search'], q ? { queryParams: { q } } : {});
    this.mobileMenuOpen = false;
  }

  /**
   * Submits to the existing /search page, carrying the typed text as a query
   * param. Navigating with no text still opens search (same as the old
   * click-anywhere behaviour) rather than dead-ending on an empty submit.
   */
  submitSearch(): void {
    this.openSearchPage();
  }

  private closeTimer: any = null;
  private baseurl: string = SystemApp.base_url;

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  };

  constructor(
      private http: HttpClient,
      private apiKey: ApiUrl,
      private loginService: LoginService,
      public cartService: CartService,
  ) {
    this.loadData();
  }

  ngOnInit(): void {}

  // ── Scroll: pin nav bar to top when user scrolls past it ────────────────────
  private navOffsetTop = 0;

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const nav         = document.querySelector('.nav-wrap')        as HTMLElement;
    const placeholder = document.querySelector('.nav-placeholder') as HTMLElement;
    if (!nav) return;

    // Capture natural position once before nav becomes fixed
    if (!this.navOffsetTop && !nav.classList.contains('is-fixed')) {
      this.navOffsetTop = nav.offsetTop;
    }

    if (window.pageYOffset >= this.navOffsetTop) {
      nav.classList.add('is-fixed');
      if (placeholder) placeholder.classList.add('visible');
    } else {
      nav.classList.remove('is-fixed');
      if (placeholder) placeholder.classList.remove('visible');
    }
  }

  // ── Category Hover ──────────────────────────────────────────────────────────
  onCatHover(cat: any): void {
    this.cancelCloseTimer();
    this.allCatsOpen = false;
    this.hoveredCategory = cat;
    // default highlight to first sub
    this.hoveredSub = cat.subcategories?.[0] || null;
  }

  startCloseTimer(): void {
    // Small grace period so moving the cursor from a category link down into
    // the mega menu (or briefly across its edge) doesn't snap it shut.
    this.cancelCloseTimer();
    this.closeTimer = setTimeout(() => {
      this.hoveredCategory = null;
      this.hoveredSub = null;
    }, 250);
  }

  cancelCloseTimer(): void {
    clearTimeout(this.closeTimer);
  }

  // ── All Categories ──────────────────────────────────────────────────────────
  toggleAllCats(): void {
    this.allCatsOpen = !this.allCatsOpen;
    if (this.allCatsOpen) this.hoveredCategory = null;
  }

  closeAllCats(): void {
    this.allCatsOpen = false;
  }

  // ── Icon Helpers ────────────────────────────────────────────────────────────
  /**
   * SVG path data for a main category. Consumed by an inline <svg><path>
   * in the template, so the icon inherits currentColor and stays visually
   * consistent with the rest of the app's stroked icons.
   */
  getCategoryIcon(category: string): string {
    return CATEGORY_ICON_PATHS[category] || CATEGORY_ICON_FALLBACK;
  }

  getSubCategoryIcon(subCategory: string): string {
    return SUBCATEGORY_ICONS[subCategory] || '•';
  }

  // ── Auth ────────────────────────────────────────────────────────────────────
  isLogin(): boolean {
    return !!localStorage.getItem('is_logged_in');
  }

  openLogin(): void {
    this.loginService.sendClickEvent(LoginAction.Open);
  }

  logout(): void {
    // POD1-7: full logout - revokes the refresh token server-side and drops
    // the socket. The keys below are profile display data, not credentials;
    // the access token lives in memory and is cleared by TokenStore.
    this.authSession.logout().subscribe();
    ['token','is_logged_in','is_superuser','first_name','last_name','email','avatar','userDetails']
        .forEach(k => localStorage.removeItem(k));
    this.accountSummary.clear();
    this.mobileMenuOpen = false;
    this.loginService.sendClickEvent(LoginAction.Close);
  }

  // ── Mobile ──────────────────────────────────────────────────────────────────
  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  navigateTo(path: string): void {
    this.router.navigate([path])
  }

  // ── Data ────────────────────────────────────────────────────────────────────
  private loadData(): void {
    this.mainCategories = [];
    this.getPages().subscribe((res: any[]) => {
      this.mainCategories = res.map((cate: any) => ({
        id:            cate.id,
        name:          cate.name,
        nameEng:       cate.category.toLowerCase(),
        category:      cate.category,
        important:     cate.important,
        icon:          cate.icon,
        mobilePng:     cate.mobilePng,
        subcategories: (cate.subcategories || []).map((sub: any) => ({
          ...sub,
          isOpen: false
        })),
        isOpen: false,
      }));
    });
  }

  private handleObserverError(error: any): Observable<any> {
    return throwError(error.error || error);
  }

  getPages(): Observable<any> {
    return this.http.get(this.apiKey.getPages(), this.options).pipe(
        map((res: any) => res || []),
        catchError(this.handleObserverError)
    );
  }

  protected readonly AppService = AppService;
}
